"use client";

/**
 * Bridge to the legacy SAP Commerce accelerator storefront cart.
 *
 * d2c-web is served from the same origin as the accelerator storefront (see
 * DOMAIN_SCOTT_SPORTS in the Vercel configs), so the browser already carries
 * the accelerator's session and routing-affinity cookies. Posting the
 * accelerator's own add-to-cart form from the browser therefore lands the
 * entry in the very session cart that the storefront cart page renders.
 *
 * A server-side adapter could target this cart only by forwarding the complete
 * session/affinity cookie chain and any Set-Cookie response. The previous
 * action did neither: it created a separate anonymous OCC cart and discarded
 * its GUID, so it could report success against a cart the browser could not
 * open.
 */

import {
  beginCartRequest,
  type CartFlow,
  type CartFlowPhase,
  type CartFlowReason,
  type CartFlowResult,
  describeBody,
  describeError,
  describeResponse,
  readBodyText,
  resourceTimingFields,
  startAddToCartFlow,
} from "@/services/datadog/cart-flow-telemetry";
import {
  getStorefrontBasePath,
  notifyCartUpdated,
  notifyProductAdded,
} from "@/utils/accelerator-cart.utils";

const CSRF_CONFIG_TOKEN_PATTERN =
  /ACC\.config\.CSRFToken\s*=\s*(["'])([^"']+)\1/;
const PRODUCT_DELIVERY_TYPE_ENABLED_PATTERN =
  /ACC\.config\.isProductDeliveryTypeEnabled\s*=\s*(["']?)(true|false)\1\s*;/gi;
const CSRF_FETCH_TIMEOUT_MS = 10_000;
const ADD_TO_CART_TIMEOUT_MS = 20_000;

export interface AddToAcceleratorCartParams {
  country: string;
  language: string;
  productCode: string;
  quantity: number;
}

export type AddToAcceleratorCartResult =
  | { status: "success" }
  | { status: "unknown" }
  | { status: "error"; errorMessage?: string };

interface AcceleratorAddToCartResponse {
  errorMsg?: string | null;
}

export interface AddToAcceleratorCartOptions {
  /** Diagnostics of this click; a flow is started internally when omitted. */
  flow?: CartFlow;
}

type DeliveryFlagState = "true" | "false" | "conflict" | "missing";

interface AcceleratorCartContext {
  csrfToken: string;
  csrfSource: "form" | "config";
  productDeliveryTypeEnabled: boolean | null;
  deliveryFlagState: DeliveryFlagState;
}

type CartContextFetchResult =
  | { kind: "ok"; context: AcceleratorCartContext }
  | { kind: "http_error" }
  | { kind: "no_csrf" }
  | { kind: "parse_error" };

function errorResult(errorMessage: string): AddToAcceleratorCartResult {
  return { status: "error", errorMessage };
}

function unknownResult(): AddToAcceleratorCartResult {
  return { status: "unknown" };
}

function isValidRequest(params: AddToAcceleratorCartParams): boolean {
  return (
    params.country.trim().length > 0 &&
    params.language.trim().length > 0 &&
    params.productCode.trim().length > 0 &&
    Number.isSafeInteger(params.quantity) &&
    params.quantity > 0
  );
}

function parseCartContext(pageHtml: string): AcceleratorCartContext | null {
  const document = new DOMParser().parseFromString(pageHtml, "text/html");
  const formToken = document
    .querySelector('input[name="CSRFToken"]')
    ?.getAttribute("value")
    ?.trim();

  const csrfToken =
    formToken || CSRF_CONFIG_TOKEN_PATTERN.exec(pageHtml)?.[2]?.trim();

  if (!csrfToken) {
    return null;
  }

  const csrfSource = formToken ? "form" : "config";

  const productDeliveryTypeFlags = new Set(
    Array.from(
      pageHtml.matchAll(PRODUCT_DELIVERY_TYPE_ENABLED_PATTERN),
      (match) => match[2]?.toLowerCase()
    )
  );
  const flagIsTrue = productDeliveryTypeFlags.has("true");
  const flagIsFalse = productDeliveryTypeFlags.has("false");
  // Conflict (both values present) → null so callers fail closed.
  let productDeliveryTypeEnabled: boolean | null = null;
  let deliveryFlagState: DeliveryFlagState = "missing";
  if (flagIsTrue && !flagIsFalse) {
    productDeliveryTypeEnabled = true;
    deliveryFlagState = "true";
  } else if (flagIsFalse && !flagIsTrue) {
    productDeliveryTypeEnabled = false;
    deliveryFlagState = "false";
  } else if (flagIsTrue && flagIsFalse) {
    deliveryFlagState = "conflict";
  }
  // Both flags present (conflicting config) or neither found → remains null

  return {
    csrfToken,
    csrfSource,
    productDeliveryTypeEnabled,
    deliveryFlagState,
  };
}

/**
 * The cart page is the currently verified locale-scoped source for both the
 * session-bound CSRF token and the Accelerator delivery-type feature flag.
 */
async function fetchCartContext(
  basePath: string,
  flow: CartFlow
): Promise<CartContextFetchResult> {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(
    () => controller.abort(),
    CSRF_FETCH_TIMEOUT_MS
  );
  const request = beginCartRequest("cart_context");
  const startedAt = performance.now();
  const url = `${basePath}/cart`;

  try {
    const response = await fetch(url, {
      cache: "no-store",
      credentials: "same-origin",
      headers: {
        Accept: "text/html",
        // Keeps the accelerator from recording this background request as the
        // session's return page, which the country switch redirects to.
        "X-Requested-With": "XMLHttpRequest",
      },
      signal: controller.signal,
    });
    request.received();
    const responseFields = {
      ...describeResponse(response),
      ...request.sessionFields(),
      durationMs: Math.round(performance.now() - startedAt),
      inFlightCartRequests: request.overlapping(),
    };

    if (!response.ok) {
      flow.step(
        "context_fetch",
        {
          ...responseFields,
          ...describeBody(response, await readBodyText(response)),
          // After the body was read, so the browser has finished the entry.
          ...resourceTimingFields(url, startedAt),
        },
        { level: "warn" }
      );
      return { kind: "http_error" };
    }

    const html = await response.text();
    let context: AcceleratorCartContext | null;

    try {
      context = parseCartContext(html);
    } catch (error) {
      flow.step(
        "context_fetch",
        {
          ...responseFields,
          ...describeBody(response, html),
          ...resourceTimingFields(url, startedAt),
          ...describeError(error),
          noResponse: false,
        },
        { level: "warn" }
      );
      return { kind: "parse_error" };
    }

    flow.step(
      "context_fetch",
      {
        ...responseFields,
        ...describeBody(response, html),
        ...resourceTimingFields(url, startedAt),
        csrfSource: context?.csrfSource ?? "none",
        deliveryFlag: context?.deliveryFlagState,
      },
      {
        level:
          context?.productDeliveryTypeEnabled === null || !context
            ? "warn"
            : "info",
        secrets: { csrfFp: context?.csrfToken },
      }
    );

    return context ? { kind: "ok", context } : { kind: "no_csrf" };
  } finally {
    request.end();
    window.clearTimeout(timeoutId);
  }
}

async function postAddToCart(
  basePath: string,
  csrfToken: string,
  productCode: string,
  quantity: number,
  deliveryType?: string
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(
    () => controller.abort(),
    ADD_TO_CART_TIMEOUT_MS
  );

  try {
    const body = new URLSearchParams({
      productCodePost: productCode,
      qty: String(quantity),
      CSRFToken: csrfToken,
    });

    if (deliveryType) {
      body.set("productDeliveryType", deliveryType);
    }

    return await fetch(`${basePath}/cart/add`, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body,
      signal: controller.signal,
    });
  } finally {
    window.clearTimeout(timeoutId);
  }
}

function parseAcceleratorResponse(
  responseText: string
): AcceleratorAddToCartResponse | null {
  try {
    const payload: unknown = JSON.parse(responseText);

    if (
      typeof payload !== "object" ||
      payload === null ||
      Array.isArray(payload)
    ) {
      return null;
    }

    const payloadRecord = payload as Record<string, unknown>;
    const hasErrorMessage = Object.hasOwn(payloadRecord, "errorMsg");

    if (
      hasErrorMessage &&
      !(
        typeof payloadRecord.errorMsg === "string" ||
        payloadRecord.errorMsg === null
      )
    ) {
      return null;
    }

    const hasAddToCartLayer = typeof payloadRecord.addToCartLayer === "string";
    const hasCartAnalyticsData =
      typeof payloadRecord.cartAnalyticsData === "object" &&
      payloadRecord.cartAnalyticsData !== null &&
      !Array.isArray(payloadRecord.cartAnalyticsData);

    if (!(hasErrorMessage || hasAddToCartLayer || hasCartAnalyticsData)) {
      return null;
    }

    return {
      errorMsg: hasErrorMessage
        ? (payloadRecord.errorMsg as string | null)
        : undefined,
    };
  } catch {
    return null;
  }
}

function hasJsonContentType(response: Response): boolean {
  const mediaType = response.headers
    .get("content-type")
    ?.split(";", 1)[0]
    ?.trim()
    .toLowerCase();

  return (
    mediaType === "application/json" || Boolean(mediaType?.endsWith("+json"))
  );
}

function decodeAcceleratorErrorMessage(errorMessage: string): string {
  const document = new DOMParser().parseFromString(errorMessage, "text/html");
  return document.body.textContent?.trim() ?? "";
}

function failureReason(
  error: unknown,
  reasons: { timeout: CartFlowReason; network: CartFlowReason }
): CartFlowReason {
  const { errorKind: category } = describeError(error);

  if (category === "timeout") {
    return reasons.timeout;
  }

  return category === "network" ? reasons.network : "unexpected_error";
}

export async function addToAcceleratorCart(
  params: AddToAcceleratorCartParams,
  fallbackErrorMessage: string,
  options?: AddToAcceleratorCartOptions
): Promise<AddToAcceleratorCartResult> {
  const flow =
    options?.flow ??
    startAddToCartFlow({
      country: params.country,
      language: params.language,
      productCode: params.productCode,
      quantity: params.quantity,
    });
  let postAttempted = false;
  let cartRefreshNotified = false;
  let phase: CartFlowPhase = "validation";
  let phaseStartedAt = performance.now();
  // The request in flight, so a failure without a response can still look up
  // what the browser measured for it.
  let lastRequest: { url: string; startedAt: number } | undefined;

  function enterPhase(next: CartFlowPhase) {
    phase = next;
    phaseStartedAt = performance.now();
  }

  function finishFlow(
    outcome: CartFlowResult["outcome"],
    reason: CartFlowReason,
    details: Partial<
      Pick<
        CartFlowResult,
        "error" | "errorMessage" | "errorName" | "outcomeKnown"
      >
    > = {}
  ) {
    flow.finish({
      outcome,
      reason,
      phase,
      postAttempted,
      outcomeKnown: false,
      ...details,
    });
  }

  function refreshCartCount() {
    if (!cartRefreshNotified) {
      notifyCartUpdated();
      cartRefreshNotified = true;
    }
  }

  if (!isValidRequest(params)) {
    finishFlow("error", "invalid_request");
    return errorResult(fallbackErrorMessage);
  }

  try {
    const basePath = getStorefrontBasePath(
      params.country.trim(),
      params.language.trim()
    );
    enterPhase("cart_context_get");
    lastRequest = { url: `${basePath}/cart`, startedAt: performance.now() };
    const contextResult = await fetchCartContext(basePath, flow);

    if (contextResult.kind === "http_error") {
      finishFlow("error", "context_http_error");
      return errorResult(fallbackErrorMessage);
    }

    enterPhase("context_parse");

    if (contextResult.kind === "parse_error") {
      finishFlow("error", "context_invalid_response");
      return errorResult(fallbackErrorMessage);
    }

    if (contextResult.kind === "no_csrf") {
      finishFlow("error", "context_no_csrf");
      return errorResult(fallbackErrorMessage);
    }

    const cartContext = contextResult.context;

    // When the flag is null the page config is ambiguous (missing or conflicting);
    // fail closed rather than guessing.
    if (cartContext.productDeliveryTypeEnabled === null) {
      finishFlow("error", "context_flag_ambiguous");
      return errorResult(fallbackErrorMessage);
    }

    // D2C is a ship-to-home storefront. When the base store has delivery-type
    // selection enabled, always POST productDeliveryType=DELIVERY.
    const deliveryType =
      cartContext.productDeliveryTypeEnabled === true ? "DELIVERY" : undefined;

    postAttempted = true;
    enterPhase("post");
    const postRequest = beginCartRequest("cart_add");
    const postUrl = `${basePath}/cart/add`;
    const postStartedAt = phaseStartedAt;
    lastRequest = { url: postUrl, startedAt: postStartedAt };
    let response: Response;
    let postDurationMs: number;

    try {
      response = await postAddToCart(
        basePath,
        cartContext.csrfToken,
        params.productCode.trim(),
        params.quantity,
        deliveryType
      );
      postRequest.received();
      postDurationMs = Math.round(performance.now() - phaseStartedAt);
    } finally {
      postRequest.end();
    }

    refreshCartCount();
    enterPhase("response_validation");
    const responseText = await response.text();
    const isJson = hasJsonContentType(response);
    const payload = isJson ? parseAcceleratorResponse(responseText) : null;
    const confirmedResponse = response.ok && payload !== null;

    flow.step(
      "post",
      {
        ...describeResponse(response),
        ...describeBody(response, responseText),
        ...resourceTimingFields(postUrl, postStartedAt),
        ...postRequest.sessionFields(),
        durationMs: postDurationMs,
        payloadShape: payloadShape(isJson, payload),
        hasErrorMsg: Boolean(payload?.errorMsg),
        deliveryType: deliveryType ?? "none",
        inFlightCartRequests: postRequest.overlapping(),
      },
      {
        level: confirmedResponse ? "info" : "warn",
        secrets: { csrfFp: cartContext.csrfToken },
      }
    );

    // Never retry this POST automatically. A 403 is not an unambiguous CSRF
    // signal, and rendered CSRF values may be masked differently per request;
    // retrying could therefore add the same item twice.
    if (!(response.ok && payload)) {
      phase = response.ok ? "response_validation" : "post";
      finishFlow(
        "unknown",
        response.ok ? "post_bad_payload" : "post_http_error"
      );
      return unknownResult();
    }

    if (payload.errorMsg) {
      const errorMessage = decodeAcceleratorErrorMessage(payload.errorMsg);
      finishFlow("error", "post_accelerator_error", {
        outcomeKnown: true,
        errorMessage,
      });
      return errorResult(errorMessage || fallbackErrorMessage);
    }

    finishFlow("success", "ok", { outcomeKnown: true });
    // Only a confirmed add opens the "added to cart" drawer; a rejected add
    // (e.g. a product that cannot share the cart) keeps the user on the page.
    notifyProductAdded();
    return { status: "success" };
  } catch (error) {
    const errorFields = {
      ...describeError(error),
      // The browser may still have an entry for a request that failed.
      ...(lastRequest
        ? resourceTimingFields(lastRequest.url, lastRequest.startedAt)
        : {}),
    };
    const errorName = String(errorFields.errorName);
    const durationMs = Math.round(performance.now() - phaseStartedAt);

    // A connection failure after dispatch has an unknown mutation outcome.
    // Refreshing the count lets the header converge without retrying the POST.
    if (postAttempted) {
      refreshCartCount();
      flow.step(
        phase === "post" ? "post" : "post_response",
        { ...errorFields, durationMs },
        { level: "warn" }
      );
      finishFlow(
        "unknown",
        failureReason(error, {
          timeout: "post_timeout",
          network: "post_network",
        }),
        { errorName, error }
      );
      return unknownResult();
    }

    flow.step(
      "context_fetch",
      { ...errorFields, durationMs },
      { level: "warn" }
    );
    finishFlow(
      "error",
      failureReason(error, {
        timeout: "context_timeout",
        network: "context_network",
      }),
      { errorName, error }
    );
    return errorResult(fallbackErrorMessage);
  }
}

function payloadShape(
  isJson: boolean,
  payload: AcceleratorAddToCartResponse | null
): string {
  if (!isJson) {
    return "not_json";
  }

  return payload ? "valid" : "unparseable";
}
